import { ResultRow } from '../api/ForecastApi';

// Backend returns rows ordered by total DESC, so the first N groups are the top N.
export const MAX_GROUPS = 30;

export function resolveColumn(
  keys: string[],
  preferred: string,
  match?: (k: string) => boolean
): string | undefined {
  if (keys.includes(preferred)) return preferred;
  return match ? keys.find(match) : undefined;
}

export function distinctGroups(rows: ResultRow[], groupCol: string): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  rows.forEach((r) => {
    const g = String(r[groupCol]);
    if (!seen.has(g)) {
      seen.add(g);
      order.push(g);
    }
  });
  return order;
}
function distinctCountKey(groupCol: string): string {
  if (groupCol === 'fgid') return 'distinct_fgids';
  if (groupCol === 'site_id') return 'distinct_sites';
  return 'distinct_groups';
}

export function aggregateByDate(
  rows: ResultRow[],
  dateCol: string,
  metricCols: string[],
  groupCol?: string
): ResultRow[] {
  const byDate = new Map<string, ResultRow>();
  rows.forEach((r) => {
    const date = String(r[dateCol]);
    let agg = byDate.get(date);
    if (!agg) {
      agg = { [dateCol]: date };
      byDate.set(date, agg);
    }
    metricCols.forEach((m) => {
      agg![m] = (Number(agg![m]) || 0) + (Number(r[m]) || 0);
    });
  });

  const aggregated = Array.from(byDate.values()).sort((a, b) =>
    String(a[dateCol]).localeCompare(String(b[dateCol]))
  );

  if (groupCol) {
    const countKey = distinctCountKey(groupCol);
    const distinctCount = distinctGroups(rows, groupCol).length;
    aggregated.forEach((row) => {
      row[countKey] = distinctCount;
    });
  }

  return aggregated;
}

export interface CapResult {
  rows: ResultRow[];
  total: number;
  capped: boolean;
}

export function capToTopGroups(
  rows: ResultRow[],
  groupCol: string | undefined,
  max = MAX_GROUPS
): CapResult {
  if (!groupCol) return { rows, total: 1, capped: false };
  const order = distinctGroups(rows, groupCol);
  if (order.length <= max) {
    return { rows, total: order.length, capped: false };
  }
  const top = new Set(order.slice(0, max));
  return {
    rows: rows.filter((r) => top.has(String(r[groupCol]))),
    total: order.length,
    capped: true,
  };
}
